import React from 'react';

/**
 * Reusable Select (Dropdown) Component.
 * Styles with Tailwind CSS, supporting various types and focus states.
 *
 * @param {object} props - Component props.
 * @param {string} [props.id] - ID for the select, also used for label's htmlFor.
 * @param {string} [props.name] - Name attribute for the select.
 * @param {string} [props.label] - Optional label text for the select.
 * @param {string} [props.value] - Controlled component value.
 * @param {function} [props.onChange] - Change event handler.
 * @param {Array<{label: string, value: string | number}>} [props.options=[]] - Array of options for the select.
 * @param {boolean} [props.required=false] - If true, select is required.
 * @param {boolean} [props.disabled=false] - If true, select is disabled.
 * @param {string} [props.className=''] - Additional custom Tailwind classes for the select element.
 * @param {string} [props.labelClassName=''] - Additional custom Tailwind classes for the label element.
 * @param {string} [props.containerClassName=''] - Additional custom Tailwind classes for the wrapper div.
 * @param {string} [props.placeholder=''] - Placeholder text for the default (empty) option.
 */
const Select = ({
    id,
    name,
    label,
    value,
    onChange,
    options = [],
    required = false,
    disabled = false,
    className = '',
    labelClassName = '',
    containerClassName = '',
    placeholder = 'Select an option',
    ...props
}) => {
    const selectBaseStyles = 'block w-full px-4 py-2 border border-gray-medium rounded-lg ' +
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue ' +
        'transition-all duration-200 ease-in-out ' +
        'text-text-primary disabled:bg-gray-light disabled:cursor-not-allowed ' +
        'appearance-none bg-no-repeat bg-right-center pr-10'; // Added appearance-none and padding for custom arrow

    // Custom arrow SVG (Tailwind doesn't have a direct class for this)
    const arrowSvg = `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'%3e%3cpath d='M7 7l3 3 3-3m0 6l-3 3-3-3' stroke='%236B7280' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3e%3c/svg%3e")`;

    return (
        <div className={`mb-4 ${containerClassName}`}>
            {label && (
                <label htmlFor={id || name} className={`block text-sm font-medium text-text-secondary mb-1 ${labelClassName}`}>
                    {label}
                    {required && <span className="text-gmail-red ml-1">*</span>}
                </label>
            )}
            <div className="relative">
                <select
                    id={id || name}
                    name={name}
                    value={value}
                    onChange={onChange}
                    required={required}
                    disabled={disabled}
                    className={`${selectBaseStyles} ${className}`}
                    style={{ backgroundImage: arrowSvg, backgroundSize: '1.5rem', backgroundPosition: 'right 0.75rem center' }}
                    {...props}
                >
                    {placeholder && <option value="" disabled={required}>{placeholder}</option>}
                    {options.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default Select;
